import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "@surgia/ui/containers/sidebar";
import { useState, useMemo } from "react";
import dashboardIcon from "@surgia/ui/icons/sidebar/dashboard.svg";
import branchesIcon from "@surgia/ui/icons/sidebar/branches.svg";
import usersIcon from "@surgia/ui/icons/sidebar/users.svg";
import catalogIcon from "@surgia/ui/icons/sidebar/catalog.svg";
import purchaseOrderIcon from "@surgia/ui/icons/sidebar/purchaseOrder.svg";
import purchaseRequestIcon from "@surgia/ui/icons/sidebar/purchaseRequest.svg";
import { useTranslation } from "react-i18next";
import { SidebarItemType } from "@surgia/ui/components/sidebar/sidebarNavItem/constants";
import { Outlet } from "react-router-dom";
import { HUB_PROTECTED_ROUTES, HUB_PUBLIC_ROUTES } from "../routes";
import { permissions } from "../permissions";
// import { getLocalStorageItem } from "../pages/Catalog";
import { useQuery } from "@tanstack/react-query";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../store";
import { branchQueries } from "@surgia/api/src/queryStore/shared/branch";
import { setGlobalState } from "../store/auth";
import config from "@surgia/config";

export const Layout = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isRTL = i18n.language === "ar";
  const authState = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const userPermissions = useMemo(() => {
    return authState.globalState.user_permissions?.map((p) => p.name) || [];
  }, [authState.globalState.user_permissions]);
  const tenant_id = authState.globalState.tenant_id;
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const isAuthenticated =
    authState.globalState.access_token && authState.globalState.subject_id;

  console.log("📐 [LAYOUT] Rendering Layout component", {
    path: location.pathname,
    isAuthenticated: isAuthenticated,
    hasAccessToken: !!authState.globalState.access_token,
    hasSubjectId: !!authState.globalState.subject_id,
    tenant_id: tenant_id,
    userPermissionsCount: userPermissions.length,
    userBranches: authState.globalState.user_branches
  });

  // Create user profile from Redux global state instead of useAuth
  const userProfile = isAuthenticated
    ? {
        name:
          authState.globalState.full_name ||
          `${authState.globalState.first_name || ""} ${authState.globalState.last_name || ""}`.trim(),
        email: authState.globalState.email,
        first_name: authState.globalState.first_name,
        last_name: authState.globalState.last_name,
        full_name: authState.globalState.full_name,
        subject_id: authState.globalState.subject_id,
      }
    : null;

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleSignOut = () => {
    console.log("🚪 [LAYOUT] Sign out clicked - navigating to sign out page");
    // Navigate to the sign-out page instead of handling it directly
    navigate(HUB_PUBLIC_ROUTES.SIGN_OUT);
  };

  const sidebarNavigationItems = useMemo(
    () => [
      {
        type: "link" as SidebarItemType,
        href: "/",
        icon: dashboardIcon,
        label: t("shared.sidebar.dashboard"),
        isActive: location.pathname === "/",
      },
      {
        type: "link" as SidebarItemType,
        href: HUB_PROTECTED_ROUTES.BRANCHES,
        icon: branchesIcon,
        label: t("shared.sidebar.branches"),
        isActive: location.pathname === HUB_PROTECTED_ROUTES.BRANCHES,
        requiredPermissions: [permissions.VIEW_BRANCH_USERS],
      },
      {
        type: "link" as SidebarItemType,
        href: HUB_PROTECTED_ROUTES.USERS,
        icon: usersIcon,
        label: t("shared.sidebar.users"),
        isActive: location.pathname === HUB_PROTECTED_ROUTES.USERS,
        requiredPermissions: [permissions.VIEW_BRANCH_USERS],
      },
      {
        type: "link" as SidebarItemType,
        href: HUB_PROTECTED_ROUTES.CATALOG,
        icon: catalogIcon,
        label: t("shared.sidebar.catalog"),
        isActive: location.pathname === HUB_PROTECTED_ROUTES.CATALOG,
      },
      {
        type: "link" as SidebarItemType,
        href: HUB_PROTECTED_ROUTES.PURCHASE_REQUESTS,
        icon: purchaseRequestIcon,
        label: t("shared.sidebar.purchaseRequests"),
        isActive: location.pathname === HUB_PROTECTED_ROUTES.PURCHASE_REQUESTS,
        requiredPermissions: [permissions.VIEW_PURCHASE_REQUEST],
      },
      {
        type: "link" as SidebarItemType,
        href: HUB_PROTECTED_ROUTES.ORDERS,
        icon: purchaseOrderIcon,
        label: t("shared.sidebar.purchaseOrders"),
        isActive: location.pathname === HUB_PROTECTED_ROUTES.ORDERS,
        requiredPermissions: [permissions.VIEW_PURCHASE_ORDER],
      },
    ],
    [authState.globalState.user_permissions?.length]
  );

  // Instead, get the selected branch from global state
  const selectedBranch =
    authState.globalState.last_accessed_branch_id ||
    authState.globalState.user_branches;

  // When you need to update the selected branch, dispatch an action to update global state
  const handleBranchChange = (newBranch: number | null) => {
    // Dispatch action to update global state
    dispatch(
      setGlobalState({
        first_name: authState.globalState.first_name!,
        last_name: authState.globalState.last_name!,
        full_name: authState.globalState.full_name!,
        email: authState.globalState.email!,
        mobile: authState.globalState.mobile!,
        subject_id: authState.globalState.subject_id!,
        creation_date: authState.globalState.creation_date!,
        user_groups: authState.globalState.user_groups || [],
        user_permissions: authState.globalState.user_permissions || [],
        last_login: authState.globalState.last_login!,
        user_branches: authState.globalState.user_branches!,

        // Token data from auth payload
        access_token: authState.globalState.access_token!,
        id_token: authState.globalState.id_token!,
        refresh_token: authState.globalState.refresh_token!,
        expires_at: authState.globalState.expires_at!,
        tenant_id: authState.globalState.tenant_id!,
        storefront_id: authState.globalState.storefront_id,
        currency_code: authState.globalState.currency_code,
        exchange_rate: authState.globalState.exchange_rate,
        last_accessed_branch_id: newBranch!,
      })
    );

    // Also update localStorage for persistence
    // localStorage.setItem("selectedBranch", newBranch);
  };
  // Fetch branches dropdown (this will use updated branches_ids)
  console.log("🏢 [LAYOUT] Branches query config:", {
    tenant_id: tenant_id,
    component_id: config.componentId,
    branches_ids: authState.globalState.user_branches,
    enabled: !!tenant_id && !!authState.globalState.user_branches
  });
  
  const { data: branchesDropdown, isLoading: isBranchesLoading } = useQuery({
    ...branchQueries.queries.getBranchesForDropdown({
      tenant_id: tenant_id!,
      component_id: config.componentId,
      branches_ids: authState.globalState.user_branches || "",
    }),
    staleTime: 5 * 60 * 1000,
    enabled: !!tenant_id && !!authState.globalState.user_branches,
  });
  
  console.log("🏢 [LAYOUT] Branches query result:", {
    isLoading: isBranchesLoading,
    hasData: !!branchesDropdown,
    branchCount: branchesDropdown?.data?.length
  });

  console.log("📐 [LAYOUT] Rendering sidebar:", {
    isAuthenticated: isAuthenticated,
    userProfile: userProfile,
    sidebarItemsCount: sidebarNavigationItems.length
  });

  if (isAuthenticated) {
    console.log("✅ [LAYOUT] Rendering Sidebar component");
  } else {
    console.log("⚠️ [LAYOUT] NOT rendering sidebar - user not authenticated");
  }

  return (
    <div className="min-h-screen bg-gray-100 p-1" dir={isRTL ? "rtl" : "ltr"}>
      <div className="flex h-[calc(100vh-0.5rem)] bg-white rounded-lg overflow-hidden">
        {/* Sidebar Container */}
        {isAuthenticated && (
          <div className="p-1 bg-gray-100 transition-all duration-300 ease-in-out">
            <Sidebar
              className="rounded-lg h-[99.9%]"
              isCollapsed={isSidebarCollapsed}
              onToggleCollapse={toggleSidebar}
              items={sidebarNavigationItems}
              userProfile={userProfile}
              onSignOut={handleSignOut}
              userPermissions={userPermissions}
            />
          </div>
        )}

        {/* Main content */}
        <div className="flex-1 overflow-auto bg-white transition-all duration-300 ease-in-out">
          <main className="p-6 pb-4">
            <Outlet
              context={{
                branchesDropdown: branchesDropdown?.data || [],
                selectedBranch,
                setSelectedBranch: handleBranchChange,
                isbranchesDropDownLoading: isBranchesLoading,
              }}
            />
          </main>
        </div>
      </div>
    </div>
  );
};

export default Layout;
